import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileEdit, Users, MessageSquare, FileText, CreditCard, LogOut, ExternalLink, Sun, Moon } from "lucide-react";
import Logo3D from "@/components/Logo3D";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";

const navItems = [
  { to: "/admin", label: "Главная", icon: LayoutDashboard, end: true },
  { to: "/admin/content", label: "Контент", icon: FileEdit },
  { to: "/admin/team", label: "Команда", icon: Users },
  { to: "/admin/reviews", label: "Отзывы", icon: MessageSquare },
  { to: "/admin/payments", label: "Платежи", icon: CreditCard },
  { to: "/admin/license", label: "Лицензия", icon: FileText },
];

export default function AdminLayout() {
  const { logout, user } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col lg:flex-row">
      <aside className="lg:w-64 lg:fixed lg:inset-y-0 lg:left-0 bg-card border-r border-border flex flex-col lg:flex-col z-30" data-testid="admin-sidebar">
        {/* Top row: logo + mobile controls */}
        <div className="px-4 lg:px-6 py-4 border-b border-border flex items-center justify-between gap-2">
          <Logo3D size={32} />
          <div className="flex items-center gap-1 lg:hidden">
            <a href="/" target="_blank" rel="noopener noreferrer" className="size-9 rounded-lg border border-border flex items-center justify-center hover:bg-muted" data-testid="admin-view-site-mobile" aria-label="View site">
              <ExternalLink className="size-4" />
            </a>
            <button onClick={toggle} className="size-9 rounded-lg border border-border flex items-center justify-center hover:bg-muted" data-testid="admin-theme-toggle-mobile" aria-label="Toggle theme">
              {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </button>
            <button onClick={handleLogout} className="size-9 rounded-lg border border-border flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground hover:border-destructive" data-testid="admin-logout-mobile" aria-label="Logout">
              <LogOut className="size-4" />
            </button>
          </div>
        </div>

        {/* Nav: horizontal scroll on mobile, vertical on desktop */}
        <nav className="flex lg:flex-col p-2 lg:p-4 gap-1 flex-1 overflow-x-auto lg:overflow-x-visible">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap shrink-0 transition-colors ${
                  isActive ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`
              }
              data-testid={`admin-nav-${it.label.toLowerCase().split(" ")[0]}`}
            >
              <it.icon className="size-4" />
              {it.label}
            </NavLink>
          ))}
        </nav>

        {/* Desktop bottom panel */}
        <div className="p-4 border-t border-border space-y-2 hidden lg:block">
          <div className="px-3 py-2 text-xs text-muted-foreground">
            <div className="font-medium text-foreground truncate">{user?.email}</div>
            <div className="uppercase tracking-wider mt-1 text-[10px] font-semibold">{user?.role}</div>
          </div>
          <div className="flex gap-2">
            <a href="/" target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-lg border border-border text-xs font-medium hover:bg-muted transition-colors" data-testid="admin-view-site">
              <ExternalLink className="size-3.5" /> Сайт
            </a>
            <button onClick={toggle} className="size-9 rounded-lg border border-border flex items-center justify-center hover:bg-muted" data-testid="admin-theme-toggle">
              {theme === "dark" ? <Sun className="size-3.5" /> : <Moon className="size-3.5" />}
            </button>
            <button onClick={handleLogout} className="size-9 rounded-lg border border-border flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground hover:border-destructive" data-testid="admin-logout">
              <LogOut className="size-3.5" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 lg:ml-64 p-4 sm:p-6 lg:p-8 min-w-0" data-testid="admin-main">
        <Outlet />
      </main>
    </div>
  );
}
